import dayjs from "dayjs";
import Loading from 'quasar/src/plugins/loading/Loading.js';
import { computed, defineComponent, reactive, ref, toRefs, watch } from "vue";
import { onBeforeRouteLeave, useRouter } from "vue-router";
import { api } from "~/api/Api";
import { GetHanyoParam } from "~/api/Common/Commands";
import { FindFileItemCommand, FindFilesInfoCommand } from "~/api/Common/Commands/FindFilesInfoCommand";
import { FindFilesInfoResult } from "~/api/data/Common/FindFIlesInfoResult";
import BreadcrumbList from "~/components/BreadcrumbList.vue";
import FilePreview from "~/components/FilePreview.vue";
import { MsgBox } from "~/components/MsgBox";
import RoomSearchInput from "~/components/RoomSearchInput.vue";
import { isNullOrUndefined } from "~/extensions/utils/DefineFunction";
import { FileCode, FileFetchKb, HanyoKb, KeyReturnAdrKb, KeyReturnNameKb, PageID, RenrakuKb, TaikyoRiyuKb } from "~/extensions/utils/DefineTypes";
import { HanyoHelper } from "~/extensions/utils/HanyoHelper";
import { isMatchKeyword } from "~/extensions/utils/KeywordSearch";
import { Log } from "~/extensions/utils/Log";
import { store } from "~/pages/taikyo/store/TaikyoStore";
import { commonStore } from "~/store/CommonStore";
import { myPageStore } from "~/store/MyPageStore";
import TaikyoNotificationCheckCard from "./components/TaikyoNotificationCheckCard.vue";
import TaikyoNotificationCheckList from "./components/TaikyoNotificationCheckList.vue";
import { useTaikyoTableColumns } from "./Models/useTaikyoTableColumns";
import { updateKaiyakuYmd } from "./Models/TaikyoFormHelper";
const log = Log.create("TaikyoNotificationCheckPage");
export default defineComponent({
    name: "TaikyoNotificationCheckPage",
    components: {
        TaikyoNotificationCheckCard,
        TaikyoNotificationCheckList,
        FilePreview,
        BreadcrumbList,
        RoomSearchInput
    },
    beforeRouteEnter(to, from, next) {
        // ここは必要だと思う。申請中の場合は次画面
        if (store.taikyoNotificationEntity.taikyoList.some(x => x.status > 2)) {
            next();
            return;
        }
        const fromScreen = from.name;
        const relatedScreens = ["taikyo-notification-flow-guide", "taikyo-notification-entry", "taikyo-applied-list"];
        if (relatedScreens.includes(fromScreen) && store.privacyConsent) {
            next();
            return;
        }
        next({ name: "taikyo" });
    },
    async setup() {
        const router = useRouter();
        const taikyo = reactive(store.taikyoNotificationEntity);
        const taikyoForInput = reactive(store.taikyoNotificationForInput);
        const shinsei = !isNullOrUndefined(taikyo.taikyoList[0]);
        const isFromBulkRoom = history.state.isFromBulkRoom;
        const isMultipleKeiyaku = myPageStore.isMultipleKeiyaku;
        const isMMA = myPageStore.personal.isMma;
        const pageName = commonStore.pageName(PageID.taikyoNotificationEntryCheck);
        // 画面状態
        const expansionState = ref(true);
        const confirmed = ref(false);
        const viewMode = ref("card");
        const lastMode = ref("card");
        const filter = ref("");
        const isKouzaChangeDialog = ref(false);
        const isKouzaChange = ref(false);
        // 口座表示
        const isKouzaUnResgistered = myPageStore.personal.bankCd === 0 &&
            myPageStore.personal.bansCd === 0 &&
            myPageStore.personal.kouzaSb === 0 &&
            myPageStore.personal.kouzaNo === 0 &&
            (myPageStore.personal.kouzaName === "" || isNullOrUndefined(myPageStore.personal.kouzaName)) &&
            (myPageStore.personal.kouzaKana === "" || isNullOrUndefined(myPageStore.personal.kouzaKana));
        // カラムの表示制御
        const filteredInput = computed(() => taikyoForInput.inputList.filter(x => taikyo.taikyoList.some(y => y.taikyoNo === x.orgNyukyokeiyakuNo && !y.isDeleted)));
        const columns = useTaikyoTableColumns(taikyo, isMultipleKeiyaku, isMMA, filteredInput, {
            showDelete: false,
            showPrecautions: false,
            keyReturnAdrWidth: "400px",
            emailWidth: "480px"
        });
        // ルート変更時のファイル削除対応
        onBeforeRouteLeave(async (to, from, next) => {
            // 申請でない場合はスキップ
            if (!shinsei) {
                next();
                return;
            }
            const toScreen = to.name;
            // ログアウト時は別処理で対応
            if (toScreen === "login") {
                next();
                return;
            }
            // 入力画面、完了画面への遷移時はスキップ
            const relatedScreens = ["taikyo-notification-entry", "taikyo-entry-complete"];
            if (relatedScreens.includes(toScreen)) {
                next();
                return;
            }
            // ファイルを削除
            store.taikyoNotificationEntity.taikyoList.forEach(x => {
                const fileList = x.fileList;
                fileList.forEach(y => {
                    y.delFlg = y.newFlg;
                });
            });
            await store.deleteFile();
            next();
        });
        watch(viewMode, val => {
            if (val !== "fullscreen") {
                lastMode.value = viewMode.value;
            }
        });
        // 口座種別の取得
        const gethanyoParam = [new GetHanyoParam(HanyoKb.KouzaSb)];
        const hanyoList = await HanyoHelper.getHanyoList(gethanyoParam);
        const bankType = hanyoList.GetByHanyoKb(HanyoKb.KouzaSb);
        const bankList = ref([]);
        bankList.value = bankType
            .filter(x => [1, 2].includes(x.value))
            .map(x => ({
            ...x,
            label: x.option1
        }));
        const customFilter = (rows, terms) => {
            return rows.filter(row => {
                return isMatchKeyword(row.bukkenName, filter.value) || isMatchKeyword(row.roomNumber, filter.value) || isMatchKeyword(row.nyukyosha, filter.value);
            });
        };
        //** 選択している入居契約情報の住所を返す */
        const getAddress = () => {
            let postCode = taikyo.taikyoList[0].postCode;
            let adr1 = taikyo.taikyoList[0].adr1;
            let adr2 = taikyo.taikyoList[0].adr2;
            let adr3 = taikyo.taikyoList[0].adr3;
            let address = "";
            if (!isNullOrUndefined(postCode)) {
                address += "〒" + postCode + "\r\n";
            }
            if (!isNullOrUndefined(adr1)) {
                address += adr1;
            }
            if (!isNullOrUndefined(adr2)) {
                address += "\r\n" + adr2;
            }
            if (!isNullOrUndefined(adr3)) {
                address += "\r\n" + adr3;
            }
            return address;
        };
        const kouzaCheck = () => {
            isKouzaChange.value =
                myPageStore.personal.bankCd !== taikyo.henkinBankCd ||
                    myPageStore.personal.bansCd !== taikyo.henkinBansCd ||
                    myPageStore.personal.kouzaSb !== taikyo.henkinKouzaSb ||
                    myPageStore.personal.kouzaNo !== Number(taikyo.henkinKouzaNo) ||
                    myPageStore.personal.kouzaName !== taikyo.henkinKouzaName ||
                    myPageStore.personal.kouzaKana !== taikyo.henkinKouzaKana;
            if (myPageStore.personal.isMma) {
                apply(false);
            }
            else if (taikyo.isKouzaNotRequired) {
                apply(false);
            }
            else if (!myPageStore.isMultipleKeiyaku) {
                apply(true);
            }
            else if (isKouzaUnResgistered) {
                apply(true);
            }
            else if (isKouzaChange.value) {
                isKouzaChangeDialog.value = true;
            }
            else {
                apply(true);
            }
        };
        const apply = async (isKouzaChange) => {
            try {
                Loading.show();
                await store.insertNotification(isKouzaChange);
                router.push({ name: "taikyo-entry-complete", state: { isSave: store.isSave() } });
                store.clear();
                store.selectedRooms = [];
                // 個人情報ストアのロード
                myPageStore.load();
                await myPageStore.waitForBasic();
            }
            catch (ex) {
                log.error("apply", ex);
                MsgBox.error(ex?.message ?? "エラーが発生しました。");
                if (ex?.data?.code === "error.mail" || ex?.data?.code === "error.partial" || ex?.data?.code === "error.bank") {
                    router.push({ name: "taikyo-entry-complete", state: { isSave: store.isSave() } });
                    store.clear();
                    store.selectedRooms = [];
                    // 個人情報ストアのロード
                    myPageStore.load();
                    await myPageStore.waitForBasic();
                }
            }
            finally {
                Loading.hide();
            }
        };
        const visibleHasonFileDialog = ref(false);
        const visibleOtherFileDialog = ref(false);
        const result = ref(new FindFilesInfoResult());
        const findFilesInfoCommand = new FindFilesInfoCommand();
        const selectedIndex = ref("");
        const initView = async () => {
            try {
                if (!shinsei) {
                    const orgNyukyokeiyakuNoList = store.selectedRooms.map(x => x.orgNyukyokeiyakuNo);
                    await store.loadTaikyoNotification(orgNyukyokeiyakuNoList);
                    await store.loadForTaikyoNotification(orgNyukyokeiyakuNoList, false);
                    updateKaiyakuYmd(taikyo.taikyoList[0]);
                }
                if (taikyo.taikyoList[0].fileList) {
                    // ファイル情報取得パラメータ設定
                    findFilesInfoCommand.fileList = [];
                    findFilesInfoCommand.OrgNyukyokeiyakuNo = Number(taikyo.taikyoList[0].taikyoNo);
                    if (taikyo.taikyoList[0].fileList.some(x => x.fileKb === 0)) {
                        findFilesInfoCommand.fileList.push(new FindFileItemCommand(FileFetchKb.Storage, FileCode.TaikyoHasonFile));
                    }
                    if (taikyo.taikyoList[0].fileList.some(x => x.fileKb === 1)) {
                        findFilesInfoCommand.fileList.push(new FindFileItemCommand(FileFetchKb.Storage, FileCode.TaikyoOthersFile));
                    }
                    // ファイル情報取得
                    result.value = await api.common.findFilesInfo(findFilesInfoCommand);
                }
            }
            catch (ex) {
                MsgBox.error(ex.message);
            }
        };
        const openHasonPreview = (index) => {
            selectedIndex.value = index;
            visibleHasonFileDialog.value = true;
        };
        const openOtherPreview = (index) => {
            selectedIndex.value = index;
            visibleOtherFileDialog.value = true;
        };
        const getShopAdr = (taikyoNo) => {
            const nyukyo = myPageStore.personal.nyukyokeiyakuList.find(x => x.orgNyukyokeiyakuNo === taikyoNo);
            if (!nyukyo) {
                return "";
            }
            return "〒" + nyukyo.shopPostcode + "  " + nyukyo.shopAdr1 + nyukyo.shopAdr2;
        };
        await initView();
        return {
            ...toRefs(store),
            router,
            pageName,
            PageID,
            shinsei,
            isFromBulkRoom,
            isMultipleKeiyaku,
            isMMA,
            taikyo,
            taikyoForInput,
            expansionState,
            confirmed,
            filter,
            viewMode,
            lastMode,
            columns,
            customFilter,
            myPageStore,
            dayjs,
            isKouzaChangeDialog,
            commonStore,
            bankList,
            kouzaCheck,
            KeyReturnNameKb,
            TaikyoRiyuKb,
            RenrakuKb,
            getAddress,
            FileCode,
            KeyReturnAdrKb,
            result,
            selectedIndex,
            openHasonPreview,
            openOtherPreview,
            visibleHasonFileDialog,
            visibleOtherFileDialog,
            isNullOrUndefined,
            store,
            getShopAdr,
            findFilesInfoCommand,
            apply
        };
    }
});
